import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useCallback, useRef, useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { Box } from "../Box";
import { Text } from "../Text";
import type { App } from "./Form";

const MAX_LENGTH = 280;

const HelperRow = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 6px 8px;
`;

const Textarea = styled("textarea")`
  padding: 8px 12px;
  background-color: var(--surface-primary);
  color: var(--text-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 8px;
  border: 0;
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  width: 100%;
  height: 32px;
  resize: none;
  box-sizing: border-box;
  overflow: hidden;
  transition: box-shadow 100ms ease-out;

  &::placeholder {
    opacity: 0.3;
  }

  &:focus {
    outline: 0.5px solid var(--control-bg);
    box-shadow: 0 0 0 3px var(--border-alpha);
    &::placeholder {
      opacity: 0.6;
    }
  }
`;

type Props = {
  app: App;
  disabled?: boolean;
};

export function NoteField({ app, disabled }: Props) {
  const z = useZero();
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [length, setLength] = useState(app.note?.length ?? 0);

  const autoGrow = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "32px";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  const save = (value: string) => {
    z.mutate(
      sharedMutators.updateConnectedApp({
        connectedAppId: app.id,
        name: app.name,
        identifier: app.identifier,
        urlPattern: app.urlPattern,
        configuration: app.configuration,
        note: value.slice(0, MAX_LENGTH),
      })
    );
  };

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md" color="var(--text-primary)">
          Note
        </Text>
      </Box>
      <Textarea
        readOnly={disabled}
        ref={(el) => {
          (
            textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>
          ).current = el;
          if (el) {
            el.style.height = "32px";
            el.style.height = `${el.scrollHeight}px`;
          }
        }}
        defaultValue={app.note ?? ""}
        placeholder="e.g. Production account, only use for live deploys"
        maxLength={MAX_LENGTH}
        rows={1}
        onChange={(e) => {
          setLength(e.target.value.length);
          autoGrow();
          clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(() => save(e.target.value), 2000);
        }}
        onBlur={(e) => {
          clearTimeout(timeoutRef.current);
          save(e.target.value);
        }}
      />
      <HelperRow>
        <Text variant="text-small" color="var(--text-secondary)">
          Helps your team and 3B know when and how to use this connector.
        </Text>
        <Text variant="text-small" color="var(--text-secondary)">
          {length} / {MAX_LENGTH}
        </Text>
      </HelperRow>
    </div>
  );
}
